// The YouTube IFrame Player API (SPEC F6), loaded once and typed by hand
// (no @types dependency). Only useYouTubePlayer touches this.

export const PlayerState = { UNSTARTED: -1, ENDED: 0, PLAYING: 1, PAUSED: 2, BUFFERING: 3, CUED: 5 } as const;

/** Errors that mean "skip this record and never dig it again" (SPEC F6). */
export const SKIP_ERRORS: readonly number[] = [100, 101, 150];

export interface YtPlayer {
  loadVideoById(opts: { videoId: string; startSeconds?: number }): void;
  /** Shows the video without playing it: allowed without a tap, and YouTube's own play button then works */
  cueVideoById(opts: { videoId: string; startSeconds?: number }): void;
  playVideo(): void;
  pauseVideo(): void;
  seekTo(seconds: number, allowSeekAhead: boolean): void;
  getCurrentTime(): number;
  getDuration(): number;
  getPlayerState(): number;
  getAvailablePlaybackRates(): number[];
  setPlaybackRate(rate: number): void;
  destroy(): void;
}

export interface YtPlayerOptions {
  width?: string | number;
  height?: string | number;
  playerVars: Record<string, string | number>;
  events: {
    onReady?: (e: { target: YtPlayer }) => void;
    onStateChange?: (e: { data: number; target: YtPlayer }) => void;
    onError?: (e: { data: number; target: YtPlayer }) => void;
  };
}

export interface YtNamespace {
  Player: new (element: HTMLElement | string, options: YtPlayerOptions) => YtPlayer;
}

declare global {
  interface Window {
    YT?: YtNamespace;
    onYouTubeIframeAPIReady?: () => void;
  }
}

export const IFRAME_API_URL = 'https://www.youtube.com/iframe_api';

let loading: Promise<YtNamespace> | undefined;

/** Loads the IFrame API script once. A failed load (offline) can be retried. */
export function loadYouTubeApi(): Promise<YtNamespace> {
  if (window.YT?.Player) return Promise.resolve(window.YT);
  loading ??= new Promise<YtNamespace>((resolve, reject) => {
    const previous = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      previous?.();
      if (window.YT) resolve(window.YT);
      else reject(new Error('YouTube API loaded without YT'));
    };
    const script = document.createElement('script');
    script.src = IFRAME_API_URL;
    script.async = true;
    script.onerror = () => {
      loading = undefined;
      script.remove();
      reject(new Error('Could not load the YouTube player'));
    };
    document.head.appendChild(script);
  });
  return loading;
}

/** A link to the record at a time: "Open on YouTube" and exported marks (`t=`). */
export const youtubeLink = (videoId: string, atS = 0): string =>
  `https://www.youtube.com/watch?v=${encodeURIComponent(videoId)}${atS > 0 ? `&t=${Math.floor(atS)}s` : ''}`;
